import { CustomersActionTypes, CustomersActions } from '@appStore/customers/actions/customers.actions';
import { Customer, CustomerFilter, CustomerUser, CustomerUserAccess, UserSearchFilter, UserInvitation, ExternalOrganisation } from '@model';

export interface State {
  loading: boolean;
  loaded: boolean;
  data: Customer[];
  selected: Customer;
  tabSelected: number;

  filterLoading: boolean;
  filterLoaded: boolean;
  filter: CustomerFilter;

  accessLoading: boolean;
  accessLoaded: boolean;
  access: CustomerUserAccess[];
  selectedAccess: string[];

  usersLoading: boolean;
  usersLoaded: boolean;
  users: CustomerUser[];
  lockedUsers: string[];

  selectedUser: CustomerUser;
  selectedUserAccessLoading: boolean;
  selectedUserAccessLoaded: boolean;
  selectedUserAccess: CustomerUserAccess[];
  selectedUserCustomers: string[];

  usersFilterLoading: boolean;
  usersFilterLoaded: boolean;
  usersFilter: UserSearchFilter;
  
  externalOrganisationsLoading: boolean;
  externalOrganisationsLoaded: boolean;
  externalOrganisations: ExternalOrganisation[];
}

export const initialState: State = {
  loading: false,
  loaded: false,
  data: [],
  selected: null,
  tabSelected: 0,

  filterLoading: false,
  filterLoaded: false,
  filter: null,

  accessLoading: false,
  accessLoaded: false,
  access: [],
  selectedAccess: [],

  usersLoading: false,
  usersLoaded: false,
  users: [],
  lockedUsers: [],

  selectedUser: null,
  selectedUserAccessLoading: false,
  selectedUserAccessLoaded: false,
  selectedUserAccess: [],
  selectedUserCustomers: [],

  usersFilterLoading: false,
  usersFilterLoaded: false,
  usersFilter: null,

  externalOrganisationsLoading: false,
  externalOrganisationsLoaded: false,
  externalOrganisations: null
};

export function reducer(state = initialState, action: CustomersActions): State {
  switch (action.type) {
    case CustomersActionTypes.CustomersLoadData:
      return { ...state, loading: true, loaded: false };
    case CustomersActionTypes.CustomersLoadSuccess:
      let _customers = action.payload;
      let newCustomersState = [];
      _customers.forEach(c => {
        if (c) {
          newCustomersState.push({ ...c, _expanded: true });
        }
      });
      return { ...state, data: newCustomersState, loading: false, loaded: true };

    case CustomersActionTypes.CustomersLoadFail:
      return { ...state, data: [], loading: false, loaded: false };
    case CustomersActionTypes.CustomersSelect:
      if (state.selected && action.payload && state.selected.id === action.payload.id) {
        return state;
      }
      return { ...state, selected: action.payload };
    case CustomersActionTypes.ClearSelectedCustomer:
      return { ...state, selected: null };
    case CustomersActionTypes.CustomersTabChanged:
        return { ...state, tabSelected: action.payload };

    case CustomersActionTypes.CustomersFilterLoadData:
      return { ...state, filterLoading: true, filterLoaded: false };
    case CustomersActionTypes.CustomersFilterLoadSuccess:
      return { ...state, filter: action.payload, filterLoading: false, filterLoaded: true };
    case CustomersActionTypes.CustomersFilterLoadFail:
      return { ...state, filter: null, filterLoading: false, filterLoaded: false };
    case CustomersActionTypes.CustomersFilterUpdate:
      return { ...state, filter: action.payload };

    case CustomersActionTypes.ClearCustomers:
      return { ...state, loaded: false, loading: false };

    case CustomersActionTypes.CustomersUserAccessLoadData:
      return { ...state, accessLoading: true, accessLoaded: false };
    case CustomersActionTypes.CustomersUserAccessLoadSuccess:
      return { ...state, access: action.payload, accessLoading: false, accessLoaded: true };
    case CustomersActionTypes.CustomersUserAccessLoadFail:
      return { ...state, access: [], accessLoading: false, accessLoaded: false };
    case CustomersActionTypes.ClearCustomersUserAccessData:
        return { ...state, access: [], accessLoading: false, accessLoaded: false };
    case CustomersActionTypes.CustomersUserAccessSelection:
          return {
            ...state,
            selectedAccess: [...action.payload]
          };

    case CustomersActionTypes.CustomersUsersLoadData:
      return { ...state, usersLoading: true, usersLoaded: false };
    case CustomersActionTypes.CustomersUsersLoadSuccess:
      return { ...state, users: action.payload, usersLoading: false, usersLoaded: true };
    case CustomersActionTypes.CustomersUsersLoadFail:
      return { ...state, users: [], usersLoading: false, usersLoaded: false };

    case CustomersActionTypes.UserLockUpdate:
        return {
          ...state,
          lockedUsers: [...action.payload]
        };

    case CustomersActionTypes.CustomersUsersSelect:
        if (state.selectedUser && action.payload && state.selectedUser.userId === action.payload.userId) {
          return state;
        }
        return { ...state, selectedUser: action.payload };
    case CustomersActionTypes.SelectedCustomersUserAccessLoadData:
          return { ...state, selectedUserAccessLoading: true, selectedUserAccessLoaded: false };
    case CustomersActionTypes.SelectedCustomersUserAccessLoadSuccess:
          return { ...state, selectedUserAccess: action.payload, selectedUserAccessLoading: false, selectedUserAccessLoaded: true };
    case CustomersActionTypes.SelectedCustomersUserAccessLoadFail:
          return { ...state, selectedUserAccess: [], selectedUserAccessLoading: false, selectedUserAccessLoaded: false };
    case CustomersActionTypes.ClearSelectedCustomersUserAccessData:
            return { ...state, selectedUserAccess: [], selectedUserAccessLoading: false, selectedUserAccessLoaded: false };
    case CustomersActionTypes.SelectedCustomersUserCustomerSelection:
      return {
        ...state,
        selectedUserCustomers: [...action.payload]
      };

    case CustomersActionTypes.CustomersUsersFilterLoadData:
      return { ...state, usersFilterLoading: true, usersFilterLoaded: false };
    case CustomersActionTypes.CustomersUsersFilterLoadSuccess:
      return { ...state, usersFilter: action.payload, usersFilterLoading: false, usersFilterLoaded: true };
    case CustomersActionTypes.CustomersUsersFilterLoadFail:
      return { ...state, filter: null, usersFilterLoading: false, usersFilterLoaded: false };
    case CustomersActionTypes.CustomersUsersFilterUpdate:
       return { ...state, usersFilter: action.payload };

    // External Organisations
    case CustomersActionTypes.CustomersExternalOrganisationsLoadData:
      return { ...state, externalOrganisationsLoading: true, externalOrganisationsLoaded: false };
    case CustomersActionTypes.CustomersExternalOrganisationsLoadSuccess:
      return { ...state, externalOrganisations: action.payload, externalOrganisationsLoading: false, externalOrganisationsLoaded: true };
    case CustomersActionTypes.CustomersExternalOrganisationsLoadFail:
      return { ...state, externalOrganisations: [], externalOrganisationsLoading: false, externalOrganisationsLoaded: false };
  }
  return state;
}

export const getCustomers = (state: State) => state.data;
export const getLoading = (state: State) => state.loading;
export const getLoaded = (state: State) => state.loaded;
export const getSelectedCustomer = (state: State) => state.selected;
export const getSelectedTab = (state: State) => state.tabSelected;

export const getFilter = (state: State) => state.filter;
export const getFilterLoading = (state: State) => state.filterLoading;
export const getFilterLoaded = (state: State) => state.filterLoaded;

export const getCustomersUserAccess = (state: State) => state.access;
export const getCustomersUserAccessLoading = (state: State) => state.accessLoading;
export const getCustomersUserAccessLoaded = (state: State) => state.accessLoaded;
export const getCustomersUserAccessSelected = (state: State) => state.selectedAccess;

export const getCustomersUsers = (state: State) => state.users;
export const getCustomersUsersLoading = (state: State) => state.usersLoading;
export const getCustomersUsersLoaded = (state: State) => state.usersLoaded;

export const getSelectedUser = (state: State) => state.selectedUser;
export const getSelectedUserAccess = (state: State) => state.selectedUserAccess;
export const getSelectedUserAccessLoading = (state: State) => state.selectedUserAccessLoading;
export const getSelectedUserAccessLoaded = (state: State) => state.selectedUserAccessLoaded;
export const getSelectedUserCustomersSelected = (state: State) => state.selectedUserCustomers;

export const getUsersFilter = (state: State) => state.usersFilter;
export const getUsersFilterLoading = (state: State) => state.usersFilterLoading;
export const getUsersFilterLoaded = (state: State) => state.usersFilterLoaded;

export const getCustomersExternalOrganisations = (state: State) => state.externalOrganisations;
export const getCustomersExternalOrganisationsLoading = (state: State) => state.externalOrganisationsLoading;
export const getCustomersExternalOrganisationsLoaded = (state: State) => state.externalOrganisationsLoaded;

export const getLockedusers = (state: State) => state.lockedUsers;
